import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ModalV2, DateRangeFilter, IconImage } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useCreateTradeDataExportMutation, useDeleteReportsMutation, useGetAllReportsQuery, useLazyGetReportDownloadUrlQuery } from "@/services";
import { download_img } from "@/constants";

interface Props {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}

const TradeDataExportModal: FC<Props> = ({ show, setShow }) => {
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const [dateRange, setDateRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
  });
  const [_, setPendingReportId] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const reportQueryVars = useMemo(() => {
    const base: any = { pageNo: 1, pageSize: 20 };
    if (user?.userRole && [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)) {
      base.clientId = selectedClient?._id;
    }
    if (user?.userRole && [roles.TDADMIN].includes(user.userRole)) {
      base.tenantId = selectedTenant?._id;
    }
    return base;
  }, [user, selectedClient?._id, selectedTenant?._id]);

  const { data: reportsData, refetch } = useGetAllReportsQuery({ ...reportQueryVars, onlyExports: true }, { skip: !user });
  const tradeExports = useMemo(() => (reportsData?.data || []).filter((r) => r.reportType === "TRADE_DATA_EXPORT"), [reportsData]);

  const [createExport, { isLoading: isCreating }] = useCreateTradeDataExportMutation();
  const [deleteReport] = useDeleteReportsMutation();
  const [getReportDownloadUrl] = useLazyGetReportDownloadUrlQuery();

  const canCreate = useMemo(() => !!(dateRange.startDate && dateRange.endDate), [dateRange]);

  const stopPolling = () => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleCreate = async () => {
    if (!canCreate || !user) return;
    const input: any = {
      dateFrom: dateRange.startDate!.toISOString().slice(0, 10),
      dateTo: dateRange.endDate!.toISOString().slice(0, 10),
    };
    if (user.userRole && [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole)) {
      input.clientId = selectedClient?._id;
    }
    if (user.userRole && [roles.TDADMIN].includes(user.userRole)) {
      input.tenantId = selectedTenant?._id;
    }
    try {
      const res = await createExport(input).unwrap();
      const newReportId = res?.data;
      if (newReportId) {
        setPendingReportId(newReportId);
        // start polling every 4s up to ~2 minutes
        const start = Date.now();
        pollRef.current = window.setInterval(async () => {
          const result = await refetch();
          const latest = (result.data?.data || []) as IReport[];
          const target = latest.find((r) => r._id === newReportId);
          const done = !!target?.s3Key;
          const timedOut = Date.now() - start > 120000; // 2 minutes
          if (done || timedOut) {
            stopPolling();
            setPendingReportId(null);
          }
        }, 4000);
      } else {
        await refetch();
      }
    } catch {}
  };

  const handleDownload = async (report: IReport) => {
    const url = await getReportDownloadUrl({ reportId: report._id }).unwrap();
    const a = document.createElement("a");
    a.href = url;
    a.download = "trade-data-export.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (report: IReport) => {
    await deleteReport(report._id).unwrap();
    await refetch();
  };

  return (
    <ModalV2
      show={show}
      setShow={setShow}
      title="Export Trade Data"
      subTitle="Create exports and download from your export history"
      buttonText="Create Export"
      buttonAction={handleCreate}
      isConfirmButtonLoading={isCreating}
      maxWidth="sm"
      hideCancelButton={false}
    >
      <Stack gap={2} sx={{ p: 2 }}>
        <Typography fontWeight={600}>Previous Exports</Typography>
        <Box sx={{ maxHeight: 280, overflowY: "auto", border: "1px solid #eee", borderRadius: 1 }}>
          {(tradeExports || []).length === 0 && (
            <Typography sx={{ p: 2, color: "#777" }}>No previous exports</Typography>
          )}
          {(tradeExports || []).map((r) => (
            <Stack key={r._id} direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderBottom: "1px solid #f2f2f2" }}>
              <Stack>
                <Typography fontWeight={500}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}</Typography>
                <Typography variant="body2" sx={{ color: "#777" }}>{r.exportDateAbbrev || ""}</Typography>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<IconImage src={download_img} size="16px" />}
                  disabled={!r.s3Key}
                  onClick={() => handleDownload(r)}
                >
                  Download
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDelete(r)}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          ))}
        </Box>

        <Typography fontWeight={600}>Create New Export</Typography>
        <DateRangeFilter
          value={{ startDate: dateRange.startDate, endDate: dateRange.endDate }}
          onSelectDate={({ startDate, endDate }) => setDateRange({ startDate, endDate })}
        />
      </Stack>
    </ModalV2>
  );
};

export default TradeDataExportModal;
